import {
  faCog,
  faFileAlt,
  faSearch,
  faSpinner,
  faTicketAlt,
  faTimes,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, Dropdown, Form, Nav } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';

import useGlobalSearch from 'hooks/useGlobalSearch';
import useSearchSettings from 'hooks/useSearchSettings';
import SearchPreferencesModal from './SearchPreferencesModal';

const GlobalSearchBox = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showScopeTabs, setShowScopeTabs] = useState(false);
  const [selectedScope, setSelectedScope] = useState('all');
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  const { searchSettings, updateSearchSettings } = useSearchSettings();
  const {
    searchResults,
    isLoading,
    error,
    debouncedSearch,
    clearResults,
    getTotalResults,
    getResultsCount,
    getSpecificCounts,
    MIN_SEARCH_LENGTH
  } = useGlobalSearch(searchSettings);

  useEffect(() => {
    console.log('searchResults:', searchResults);
  }, [searchResults]);

  useEffect(() => {
    console.log('totalResults:', getTotalResults());
  }, [getTotalResults]);

  const handleInputChange = e => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(value.length >= MIN_SEARCH_LENGTH);

    if (value.length >= MIN_SEARCH_LENGTH) {
      // Usar apenas o endpoint /search para preview
      debouncedSearch(value, 0, 10, selectedScope);
    } else {
      clearResults();
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      navigateToSearchResults();
    }
  };

  const navigateToSearchResults = () => {
    if (searchTerm.trim()) {
      clearResults();
      setShowDropdown(false);
      setShowScopeTabs(false);
      navigate(
        `/search-results?q=${encodeURIComponent(
          searchTerm.trim()
        )}&scope=${selectedScope}`
      );
    }
  };

  const handleClear = () => {
    setSearchTerm('');
    clearResults();
    setShowDropdown(false);
    setShowScopeTabs(false);
    searchInputRef.current?.focus();
  };

  const handleInputFocus = () => {
    setShowScopeTabs(true);
  };

  const handleScopeChange = scope => {
    setSelectedScope(scope);
    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      // Usar apenas o endpoint /search para preview
      debouncedSearch(searchTerm, 0, 10, scope);
    }
  };

  const handlePreferencesClick = e => {
    e.preventDefault();
    e.stopPropagation();
    setShowPreferencesModal(true);
  };

  const handlePreferencesUpdate = async newSettings => {
    const result = await updateSearchSettings(newSettings);
    if (result.success) {
      // Se há um termo de busca ativo, refazer a busca com as novas configurações imediatamente
      if (searchTerm.length >= MIN_SEARCH_LENGTH) {
        // Usar as novas configurações diretamente na busca
        debouncedSearch(searchTerm, 0, 10, selectedScope, newSettings);
      }
    }
    return result;
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
        setShowScopeTabs(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderResultItem = (item, type) => {
    const getTypeIcon = () => {
      switch (type) {
        case 'tickets':
          return faTicketAlt;
        case 'contacts':
          return faUser;
        case 'articles':
          return faFileAlt;
        default:
          return faSearch;
      }
    };

    const getTypeTitle = () => {
      switch (type) {
        case 'tickets':
          return `#${item.code} ${item.subject}`;
        case 'contacts':
          return `${item.name}`;
        case 'articles':
          return `${item.title}`;
        default:
          return 'Resultado';
      }
    };

    const getTypeLink = () => {
      switch (type) {
        case 'tickets':
          return `/tickets/${item.id}`;
        case 'contacts':
          return `/registration/contacts`;
        case 'articles':
          return `/documents/articles/${item.id}`;
        default:
          return '#';
      }
    };

    return (
      <Dropdown.Item
        key={`${type}-${item.id}`}
        className="d-flex align-items-start py-2 px-3 border-0"
        onClick={() => {
          setShowDropdown(false);
          clearResults();
        }}
      >
        <div className="d-flex align-items-center w-100">
          <div className="me-3">
            <Badge
              bg={
                type === 'tickets'
                  ? 'success'
                  : type === 'contacts'
                  ? 'info'
                  : 'warning'
              }
              className="d-flex align-items-center justify-content-center"
              style={{ width: '24px', height: '24px' }}
            >
              <FontAwesomeIcon icon={getTypeIcon()} size="xs" />
            </Badge>
          </div>
          <Link to={getTypeLink()} className="flex-grow-1 text-decoration-none">
            <div className="fw-bold mb-1 text-muted">{getTypeTitle()}</div>
            <small className="text-muted d-block">
              {item.email || item.phone || item.landline}
            </small>
          </Link>
        </div>
      </Dropdown.Item>
    );
  };

  return (
    <>
      <div
        className="position-relative w-100"
        style={{ maxWidth: '400px' }}
        ref={dropdownRef}
      >
        <Form.Control
          ref={searchInputRef}
          type="text"
          placeholder="Pesquisar tickets, contatos, artigos..."
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          className="rounded-pill ps-4 pe-5 bg-body-emphasis"
          onBlur={() => {
            setSearchTerm('');
          }}
          style={{
            fontSize: '0.875rem',
            transition: 'all 0.2s ease-in-out'
          }}
        />

        <div className="position-absolute top-50 end-0 translate-middle-y pe-3 d-flex align-items-center gap-2">
          {isLoading ? (
            <FontAwesomeIcon
              icon={faSpinner}
              className="text-muted small"
              spin
            />
          ) : searchTerm ? (
            <Button
              variant="link"
              size="sm"
              onClick={handleClear}
              className="p-0 text-muted border-0 bg-transparent"
              style={{ fontSize: '0.875rem' }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </Button>
          ) : (
            <FontAwesomeIcon icon={faSearch} className="text-muted small" />
          )}
        </div>

        {/* Abas de escopo */}
        {showScopeTabs && (
          <div className="position-absolute w-100 mt-2 bg-body-emphasis border rounded shadow-sm">
            <div className="d-flex align-items-center justify-content-between p-2 border-bottom">
              <Nav variant="pills" className="flex-grow-1">
                <Nav.Item>
                  <Nav.Link
                    active={selectedScope === 'all'}
                    onClick={() => handleScopeChange('all')}
                    className="py-1 px-3 small"
                  >
                    Todos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={selectedScope === 'tickets'}
                    onClick={() => handleScopeChange('tickets')}
                    className="py-1 px-3 small"
                  >
                    Tickets
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={selectedScope === 'contacts'}
                    onClick={() => handleScopeChange('contacts')}
                    className="py-1 px-3 small"
                  >
                    Contatos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link
                    active={selectedScope === 'articles'}
                    onClick={() => handleScopeChange('articles')}
                    className="py-1 px-3 small"
                  >
                    Artigos
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Button
                variant="link"
                size="sm"
                onClick={handlePreferencesClick}
                className="p-1 text-muted border-0 bg-transparent"
                title="Configurar preferências de pesquisa"
              >
                <FontAwesomeIcon icon={faCog} size="sm" />
              </Button>
            </div>
          </div>
        )}

        {/* Dropdown de resultados */}
        {showDropdown && (
          <Dropdown.Menu
            show={showDropdown}
            className="position-absolute w-100 mt-1 border shadow-sm"
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1050
            }}
          >
            {isLoading ? (
              <div className="text-center py-4">
                <FontAwesomeIcon icon={faSpinner} className="text-muted" spin />
                <div className="small text-muted mt-2">Pesquisando...</div>
              </div>
            ) : error ? (
              <div className="text-center py-3">
                <div className="small text-danger">
                  Erro ao pesquisar. Tente novamente.
                </div>
              </div>
            ) : searchResults && getTotalResults() > 0 ? (
              <>
                {searchResults.tickets?.length > 0 && (
                  <div className="border-bottom">
                    <div className="dropdown-header px-3 py-3 fw-bold text-muted">
                      Tickets ({getSpecificCounts().totalTicketCount})
                    </div>
                    {searchResults.tickets
                      .slice(0, 3)
                      .map(item => renderResultItem(item, 'tickets'))}
                  </div>
                )}

                {searchResults.contacts?.length > 0 && (
                  <div className="border-bottom">
                    <div className="dropdown-header px-3 py-3 fw-bold text-muted">
                      Contatos ({getSpecificCounts().totalContactCount})
                    </div>
                    {searchResults.contacts
                      .slice(0, 3)
                      .map(item => renderResultItem(item, 'contacts'))}
                  </div>
                )}

                {searchResults.articles?.length > 0 && (
                  <div className="border-bottom">
                    <div className="dropdown-header px-3 py-3 fw-bold text-muted">
                      Artigos ({getSpecificCounts().totalArticleCount})
                    </div>
                    {searchResults.articles
                      .slice(0, 3)
                      .map(item => renderResultItem(item, 'articles'))}
                  </div>
                )}

                <div className="text-center py-2 border-top">
                  <small className="text-muted">
                    Pressione Enter para ver todos os resultados
                  </small>
                </div>
              </>
            ) : searchTerm.length >= MIN_SEARCH_LENGTH ? (
              <div className="text-center py-4">
                <div className="small text-muted">
                  Nenhum resultado encontrado
                </div>
              </div>
            ) : null}
          </Dropdown.Menu>
        )}
      </div>

      {/* Modal de preferências */}
      <SearchPreferencesModal
        show={showPreferencesModal}
        onHide={() => setShowPreferencesModal(false)}
        onUpdate={handlePreferencesUpdate}
      />
    </>
  );
};

export default GlobalSearchBox;
